import React, { useEffect, useMemo, useRef, useState } from 'react';
// Replaced react-select with custom headless dropdown for pill-style filters
import { twMerge } from 'tailwind-merge';
import { useCodeDefinitions } from '../hooks/useCodeDefinitions';
import { useDebounce } from '../hooks/useDebounce';
import { useWorkflowMetadata } from '../hooks/useWorkflowMetadata';
import { Fieldset, Field, Label } from './fieldset';
import FilterDropdown from './filter-dropdown';
import { Input, InputGroup } from './input';

/**
 * Renders Status and Reason filter dropdowns. Only mounted when workflow_scope is provided,
 * so useWorkflowMetadata is only called when we have a valid scope to fetch.
 */
const ListFilterStatusReasonSection: React.FC<{
  workflow_scope: string;
  showStatus: boolean;
  showReason: boolean;
  controlledStatuses: string[];
  controlledReasons: string[];
  onStatusChange: (selected: string[]) => void;
  onReasonChange: (selected: string[]) => void;
  labelOverrides: Record<string, string>;
}> = ({
  workflow_scope,
  showStatus,
  showReason,
  controlledStatuses,
  controlledReasons,
  onStatusChange,
  onReasonChange,
  labelOverrides,
}) => {
  const { statuses } = useWorkflowMetadata(workflow_scope);

  const statusOptions = useMemo(() => {
    if (!statuses) return [];
    return Object.values(statuses)
      .filter((status): status is NonNullable<typeof status> => status !== undefined)
      .map((status) => ({
        value: status.code,
        label: status.title,
        is_active: true,
      }));
  }, [statuses]);

  const reasonOptions = useMemo(() => {
    if (!statuses) return [];
    const reasonsMap = new Map<string, string>();
    Object.values(statuses)
      .filter((status): status is NonNullable<typeof status> => status !== undefined)
      .forEach((status) => {
        status.reasons?.forEach((reason) => {
          if (!reasonsMap.has(reason.code)) {
            reasonsMap.set(reason.code, reason.title);
          }
        });
      });
    return Array.from(reasonsMap.entries())
      .map(([code, title]) => ({
        value: code,
        label: title,
        is_active: true,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [statuses]);

  return (
    <>
      {showStatus && (
        <Field className="tw:flex-none">
          <Label className="tw:sr-only">{labelOverrides['Status'] || 'Status'}</Label>
          <FilterDropdown
            label={labelOverrides['Status'] || 'Status'}
            options={statusOptions}
            value={controlledStatuses}
            multiple
            onChange={(next) => onStatusChange(Array.isArray(next) ? next : [])}
          />
        </Field>
      )}
      {showReason && (
        <Field className="tw:flex-none">
          <Label className="tw:sr-only">{labelOverrides['Reason'] || 'Reason'}</Label>
          <FilterDropdown
            label={labelOverrides['Reason'] || 'Reason'}
            options={reasonOptions}
            value={controlledReasons}
            multiple
            onChange={(next) => onReasonChange(Array.isArray(next) ? next : [])}
          />
        </Field>
      )}
    </>
  );
};

export type DisplayType = 'table' | 'card';

export interface ListFilterValues {
  search?: string;
  statuses?: string[];
  reasons?: string[];
  categories?: Record<string, string[]>; // category code -> array of selected values
  displayType?: DisplayType;
  // Ordering string for Django OrderingFilter, e.g. 'updated_at' or '-updated_at'
  ordering?: string;
}

export interface ListFilterProps {
  /**
   * If true, show a text box allowing a user to search (debounced)
   */
  show_search: boolean;
  /**
   * Workflow scope string; used to fetch statuses and reasons for multiselect components
   */
  workflow_scope?: string;
  /**
   * List of code def category strings; for each category, fetch values and populate a multiselect
   * E.g. ['Specialty', 'RateType']
   *
   * NOTE: When using this component, ensure that each category in this array has a corresponding
   * API parameter mapping in the parent component's API call. The parent component should map
   * category names to API parameter names (e.g., 'Specialty' -> 'specialties'). Categories without
   * API mappings will be ignored by the API, which may lead to unexpected filtering behavior.
   */
  categories?: string[];
  /**
   * Boolean that dictates whether the Status multiselect is shown
   */
  showStatus: boolean;
  /**
   * Boolean that dictates whether the Reason multiselect is shown
   */
  showReason: boolean;
  /**
   * Callback function that exposes filter values to parent so parent can construct query params
   */
  onFilterChange: (values: ListFilterValues) => void;
  /**
   * Boolean that dictates if the list display type selector is visible or not
   */
  showDisplayType: boolean;
  /**
   * Optional default display type (defaults to 'table')
   */
  defaultDisplayType?: DisplayType;
  /**
   * Current filter values (controlled component - URL params should be the source of truth)
   */
  values?: Partial<ListFilterValues>;
  /**
   * If true, uses smaller sizing for select components (matches Input/Button small prop)
   */
  // small?: boolean;
  /**
   * Available sort fields. value = API field key (without leading '-')
   */
  sortOptions?: Array<{ label: string; value: string }>;
  /**
   * Optional map to override display labels for filters.
   * Keys should match category names (e.g., 'UrgencyLevel', 'JustificationType')
   * or special keys like 'Status' and 'Reason'. Values are the labels to display.
   */
  labelOverrides?: Record<string, string>;
}

/**
 * ListFilter Component
 *
 * A reusable controlled component for filtering lists of model objects (Connections, Opportunities, Needs, etc).
 * Provides search, status/reason filtering, category filtering, and display type toggle.
 *
 * URL params should be the single source of truth. This component receives current values from props
 * and calls onFilterChange when user interacts with filters.
 *
 * @example
 * ```tsx
 * const [searchParams, setSearchParams] = useSearchParams();
 * const search = searchParams.get('search') || '';
 * const statuses = searchParams.get('status')?.split(',') || [];
 *
 * <ListFilter
 *   show_search={true}
 *   workflow_scope="staffing.need"
 *   categories={['Specialty', 'RateType']}
 *   showStatus={true}
 *   showReason={true}
 *   showDisplayType={true}
 *   values={{
 *     search,
 *     statuses: statuses.length > 0 ? statuses : undefined,
 *   }}
 *   onFilterChange={(values) => {
 *     // Update URL params from values
 *     const params = new URLSearchParams(searchParams);
 *     if (values.search) params.set('search', values.search);
 *     else params.delete('search');
 *     // ... etc
 *     setSearchParams(params);
 *   }}
 * />
 * ```
 */
export const ListFilter: React.FC<ListFilterProps> = ({
  show_search,
  workflow_scope,
  categories = [],
  showStatus,
  showReason,
  onFilterChange,
  showDisplayType,
  defaultDisplayType = 'table',
  values = {},
  // small = true,
  sortOptions = [],
  labelOverrides = {},
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  // Get controlled values from props (URL params are the source of truth)
  // Wrap in useMemo to avoid recreating arrays/objects on every render
  const controlledSearch = values.search || '';
  const controlledStatuses = useMemo(() => values.statuses || [], [values.statuses]);
  const controlledReasons = useMemo(() => values.reasons || [], [values.reasons]);
  const controlledCategories = useMemo(() => values.categories || {}, [values.categories]);
  // Effective display type: rely on controlled value from parent (resolved in hook)
  const effectiveDisplayType: DisplayType = useMemo(() => {
    return (values.displayType || defaultDisplayType) as DisplayType;
  }, [values.displayType, defaultDisplayType]);

  // Ordering (single source of truth comes from URL via parent hook)
  const controlledOrdering = useMemo(() => values.ordering || '-updated_at', [values.ordering]);
  const currentSortField = controlledOrdering.startsWith('-')
    ? controlledOrdering.slice(1)
    : controlledOrdering;
  const isDesc = controlledOrdering.startsWith('-');

  // Determine if current sort label starts with '#', to decide numeric vs alpha icon
  const currentSortOption = useMemo(() => {
    return sortOptions.find((o) => o.value === currentSortField);
  }, [sortOptions, currentSortField]);
  const isNumericSortLabel = useMemo(() => {
    const label = currentSortOption?.label || '';
    return label.trim().startsWith('#');
  }, [currentSortOption]);

  // Decide if Sort controls should be shown: only in Card view and when options are provided
  const shouldShowSort = useMemo(() => {
    return effectiveDisplayType === 'card' && (sortOptions?.length ?? 0) > 0;
  }, [effectiveDisplayType, sortOptions]);

  // When search is the only filter control (no Status, Reason, or Category dropdowns),
  // let it stretch to fill the space up to the display type toggle
  const isSearchOnlyFilter = useMemo(() => {
    return show_search && !showStatus && !showReason && (categories?.length ?? 0) === 0;
  }, [show_search, showStatus, showReason, categories]);

  // Local state for search input (for debouncing - we don't want to update URL on every keystroke)
  // Sync with the controlled value when it changes externally (e.g., from URL)
  const [searchInput, setSearchInput] = useState(controlledSearch);

  // Sync search input when controlled value changes externally (e.g., browser back/forward)
  useEffect(() => {
    setSearchInput(controlledSearch);
  }, [controlledSearch]);

  const debouncedSearch = useDebounce(searchInput, 300);

  const { data: codeDefinitionsData } = useCodeDefinitions();

  // No longer using react-select styles; FilterDropdown is tailwind-based

  const categoryOptionsMap = useMemo(() => {
    const optionsMap: Record<
      string,
      Array<{ value: string; label: string; is_active?: boolean }>
    > = {};
    if (!codeDefinitionsData) return optionsMap;

    categories.forEach((category) => {
      const categoryDefs = codeDefinitionsData.byCategory.get(category) || [];
      optionsMap[category] = categoryDefs.map((def) => ({
        value: def.code,
        label: def.title,
        is_active: def.is_active,
      }));
    });

    return optionsMap;
  }, [codeDefinitionsData, categories]);

  // Track previous debounced search to detect changes
  const prevDebouncedSearchRef = useRef<string>(debouncedSearch);

  // Notify parent when debounced search changes
  // Include controlled values in dependencies so we use latest values when search changes
  useEffect(() => {
    if (prevDebouncedSearchRef.current !== debouncedSearch) {
      prevDebouncedSearchRef.current = debouncedSearch;
      onFilterChange({
        search: debouncedSearch || undefined,
        statuses: controlledStatuses.length > 0 ? controlledStatuses : undefined,
        reasons: controlledReasons.length > 0 ? controlledReasons : undefined,
        categories: Object.keys(controlledCategories).length > 0 ? controlledCategories : undefined,
        displayType: effectiveDisplayType,
        ordering: controlledOrdering,
      });
    }
  }, [
    debouncedSearch,
    controlledStatuses,
    controlledReasons,
    controlledCategories,
    effectiveDisplayType,
    controlledOrdering,
    onFilterChange,
  ]);

  const handleStatusChange = (selected: string[]) => {
    onFilterChange({
      search: debouncedSearch || undefined,
      statuses: selected.length > 0 ? selected : undefined,
      reasons: controlledReasons.length > 0 ? controlledReasons : undefined,
      categories: Object.keys(controlledCategories).length > 0 ? controlledCategories : undefined,
      displayType: effectiveDisplayType,
      ordering: controlledOrdering,
    });
  };

  const handleReasonChange = (selected: string[]) => {
    onFilterChange({
      search: debouncedSearch || undefined,
      statuses: controlledStatuses.length > 0 ? controlledStatuses : undefined,
      reasons: selected.length > 0 ? selected : undefined,
      categories: Object.keys(controlledCategories).length > 0 ? controlledCategories : undefined,
      displayType: effectiveDisplayType,
      ordering: controlledOrdering,
    });
  };

  const handleCategoryChange = (category: string, selected: string[]) => {
    const updatedCategories = { ...controlledCategories };
    if (selected.length === 0) {
      delete updatedCategories[category];
    } else {
      updatedCategories[category] = selected;
    }
    onFilterChange({
      search: debouncedSearch || undefined,
      statuses: controlledStatuses.length > 0 ? controlledStatuses : undefined,
      reasons: controlledReasons.length > 0 ? controlledReasons : undefined,
      categories: Object.keys(updatedCategories).length > 0 ? updatedCategories : undefined,
      displayType: effectiveDisplayType,
      ordering: controlledOrdering,
    });
  };

  const handleDisplayTypeChange = (newDisplayType: DisplayType) => {
    onFilterChange({
      search: debouncedSearch || undefined,
      statuses: controlledStatuses.length > 0 ? controlledStatuses : undefined,
      reasons: controlledReasons.length > 0 ? controlledReasons : undefined,
      categories: Object.keys(controlledCategories).length > 0 ? controlledCategories : undefined,
      displayType: newDisplayType,
      ordering: controlledOrdering,
    });
  };

  // Determine if any filters/search are currently active to show Clear Filters button
  const hasActiveFilters = useMemo(() => {
    const hasSearch = (searchInput || '').trim().length > 0;
    const hasStatuses = controlledStatuses.length > 0;
    const hasReasons = controlledReasons.length > 0;
    const hasCategories = Object.keys(controlledCategories).length > 0;
    return hasSearch || hasStatuses || hasReasons || hasCategories;
  }, [searchInput, controlledStatuses, controlledReasons, controlledCategories]);

  const handleClearAll = () => {
    // Clear local search input immediately
    setSearchInput('');
    // Notify parent that all filters are cleared; preserve current display type
    onFilterChange({
      search: undefined,
      statuses: undefined,
      reasons: undefined,
      categories: undefined,
      displayType: effectiveDisplayType,
      // Leave ordering untouched when clearing filters
      ordering: controlledOrdering,
    });
  };

  // Sort handlers
  const handleSortFieldChange = (nextField?: string) => {
    // If cleared, reset to default '-updated_at' (descending)
    if (!nextField || nextField.length === 0) {
      onFilterChange({
        search: debouncedSearch || undefined,
        statuses: controlledStatuses.length > 0 ? controlledStatuses : undefined,
        reasons: controlledReasons.length > 0 ? controlledReasons : undefined,
        categories: Object.keys(controlledCategories).length > 0 ? controlledCategories : undefined,
        displayType: effectiveDisplayType,
        ordering: '-updated_at',
      });
      return;
    }
    const field = nextField;
    const nextOrdering = isDesc ? `-${field}` : field;
    onFilterChange({
      search: debouncedSearch || undefined,
      statuses: controlledStatuses.length > 0 ? controlledStatuses : undefined,
      reasons: controlledReasons.length > 0 ? controlledReasons : undefined,
      categories: Object.keys(controlledCategories).length > 0 ? controlledCategories : undefined,
      displayType: effectiveDisplayType,
      ordering: nextOrdering,
    });
  };

  const handleToggleSortDirection = () => {
    const nextOrdering = isDesc ? currentSortField : `-${currentSortField}`;
    onFilterChange({
      search: debouncedSearch || undefined,
      statuses: controlledStatuses.length > 0 ? controlledStatuses : undefined,
      reasons: controlledReasons.length > 0 ? controlledReasons : undefined,
      categories: Object.keys(controlledCategories).length > 0 ? controlledCategories : undefined,
      displayType: effectiveDisplayType,
      ordering: nextOrdering,
    });
  };

  return (
    <div id="list-filter-section" className=" tw:py-1">
      {/* One-row layout (desktop) with wrapping on small screens */}
      <Fieldset
        className={twMerge(
          'tw:flex tw:flex-wrap tw:items-center tw:gap-1 tw:mb-2',
          isSearchOnlyFilter && 'tw:md:flex-nowrap'
        )}
      >
        {/* Search Box */}
        {show_search && (
          <Field className="tw:min-w-0 tw:basis-full tw:w-full tw:flex-[1_1_100%] tw:md:flex-[0_1_auto] tw:md:grow-0 tw:md:shrink tw:md:!w-auto">
            <InputGroup className="tw:!min-w-0 tw:md:!min-w-0">
              <i className="fas fa-magnifying-glass" data-slot="icon" />
              <Input
                id="list-filter-search-input"
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search..."
                className="tw:!min-w-0 tw:ml:!min-w-0"
              />
            </InputGroup>
          </Field>
        )}

        {/* Status and Reason filters - only mount when workflow_scope exists to avoid fetching with empty scope */}
        {(showStatus || showReason) && workflow_scope && (
          <ListFilterStatusReasonSection
            workflow_scope={workflow_scope}
            showStatus={showStatus}
            showReason={showReason}
            controlledStatuses={controlledStatuses}
            controlledReasons={controlledReasons}
            onStatusChange={handleStatusChange}
            onReasonChange={handleReasonChange}
            labelOverrides={labelOverrides}
          />
        )}

        {/* Category Filters */}
        {categories.map((category) => {
          const categoryOptions = categoryOptionsMap[category] || [];
          const selectedCategoryValues = controlledCategories[category] || [];

          return (
            <Field key={category} className="tw:flex-none">
              <Label className="tw:sr-only">{labelOverrides[category] || category}</Label>
              <FilterDropdown
                label={labelOverrides[category] || category}
                options={categoryOptions}
                value={selectedCategoryValues}
                multiple
                // Let the pill size to content in the flex row
                onChange={(next) => handleCategoryChange(category, Array.isArray(next) ? next : [])}
              />
            </Field>
          );
        })}

        {/* Clear Filters button moved to the left group (right-most among left controls) */}
        {hasActiveFilters && (
          <div className="tw:flex-none">
            <button
              type="button"
              onClick={handleClearAll}
              className={twMerge(
                'tw:inline-flex tw:items-center tw:gap-1 tw:!rounded-lg tw:border tw:border-zinc-950/10 ',
                'tw:px-2.5 tw:py-1.5 tw:text-sm tw:text-zinc-700 tw:hover:bg-zinc-50',
                'tw:ml-auto tw:md:ml-0',
                'tw:!text-brand-green-0 tw:!border-brand-green-0 tw:bg-white ',
                'tw:data-[active]:bg-brand-green-0/5 tw:data-[hover]:bg-brand-green-0/5'
              )}
              aria-label="Clear all filters"
              title="Clear all filters"
            >
              <i className="fas fa-times tw:text-brand-green-0" />
              <span>Clear</span>
            </button>
          </div>
        )}

        {/* Display Type Toggle and Sort (right-aligned on md+, left-aligned when wrapped/small) */}
        {(showDisplayType || shouldShowSort) && (
          <div
            className={twMerge(
              'tw:flex tw:items-center tw:gap-1 tw:flex-shrink-0',
              // When search is the only filter, keep display toggle on same row (no basis-full)
              isSearchOnlyFilter
                ? 'tw:mt-0 tw:ml-auto tw:basis-auto tw:w-auto tw:justify-end'
                : 'tw:mt-2 tw:ml-0 tw:basis-full tw:w-full tw:justify-start tw:group-data-[drawer-open=true]:mt-2 tw:group-data-[drawer-open=true]:ml-0 tw:group-data-[drawer-open=true]:basis-full tw:group-data-[drawer-open=true]:w-full tw:group-data-[drawer-open=true]:justify-start tw:md:mt-0 tw:md:ml-auto tw:md:basis-auto tw:md:w-auto'
            )}
          >
            {/* Sort controls (card & table use this as source of truth) */}
            {shouldShowSort && sortOptions.length > 0 && (
              <>
                <FilterDropdown
                  label="Sort"
                  labelOverride="Sort List By"
                  options={sortOptions.map((o) => ({
                    value: o.value,
                    label: o.label,
                    is_active: true,
                  }))}
                  pillTextClassName="tw:!text-brand-green-1"
                  value={currentSortField}
                  multiple={false}
                  onChange={(next) =>
                    handleSortFieldChange(typeof next === 'string' ? next : undefined)
                  }
                />
                <button
                  type="button"
                  onClick={handleToggleSortDirection}
                  className="tw:inline-flex tw:items-center tw:gap-1 tw:!rounded-lg tw:border tw:border-zinc-950/10 tw:bg-white tw:px-2 tw:py-1.5  tw:text-zinc-700 tw:hover:bg-zinc-50"
                  aria-label={isDesc ? 'Sort descending' : 'Sort ascending'}
                  title={isDesc ? 'Descending' : 'Ascending'}
                >
                  <i
                    className={`fas ${
                      isNumericSortLabel
                        ? isDesc
                          ? 'fa-arrow-down-9-1'
                          : 'fa-arrow-down-1-9'
                        : isDesc
                          ? 'fa-arrow-down-z-a'
                          : 'fa-arrow-down-a-z'
                    } tw:text-brand-green-1 tw:w-5 tw:h-5`}
                  />
                </button>
              </>
            )}

            {showDisplayType && (
              <div className="tw:flex tw:items-center tw:gap-1 tw:border tw:border-zinc-950/10 tw:rounded-lg tw:overflow-hidden">
                <button
                  type="button"
                  onClick={() => handleDisplayTypeChange('table')}
                  className={`tw:px-2 tw:py-1.5 tw:transition-colors tw:ease-in-out tw:duration-100 ${
                    effectiveDisplayType === 'table'
                      ? 'tw:bg-brand-green-1 tw:text-white'
                      : 'tw:bg-white tw:text-zinc-600 tw:hover:bg-zinc-50'
                  }`}
                  aria-pressed={effectiveDisplayType === 'table'}
                  aria-label="Table view"
                >
                  <i className="fas fa-bars tw:w-5 tw:h-5" />
                </button>
                <button
                  type="button"
                  onClick={() => handleDisplayTypeChange('card')}
                  className={`tw:px-2 tw:py-1.5 tw:transition-colors tw:ease-in-out tw:duration-100 ${
                    effectiveDisplayType === 'card'
                      ? 'tw:bg-brand-green-1 tw:text-white'
                      : 'tw:bg-white tw:text-zinc-600 tw:hover:bg-zinc-50'
                  }`}
                  aria-pressed={effectiveDisplayType === 'card'}
                  aria-label="Card view"
                >
                  <i className="fas fa-grid tw:w-5 tw:h-5" />
                </button>
              </div>
            )}
          </div>
        )}
      </Fieldset>
    </div>
  );
};
